import React, { useState, useEffect } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Snackbar,
  MenuItem,
} from '@mui/material';
import Layout from '../components/Layout';
import authService from '../services/authService';

function Profile() {
  const { t, i18n } = useTranslation();
  usePageTitle('Profile');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const LANGUAGES = [
    { value: 'en', label: 'English' },
    { value: 'et', label: 'Eesti keel' },
    { value: 'ru', label: 'Русский' },
    { value: 'fi', label: 'Suomi' },
    { value: 'lv', label: 'Latviešu' },
    { value: 'lt', label: 'Lietuvių' },
    { value: 'uk', label: 'Українська' },
  ];

  const [formData, setFormData] = useState({
    username: '',
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    personal_id_number: '',
    language: 'en',
    bio: '',
    date_of_birth: '',
  });

  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password1: '',
    new_password2: '',
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await authService.getUserInfo();
      setProfile(data);
      setFormData({
        username: data.username || '',
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        email: data.email || '',
        phone_number: data.phone_number || '',
        personal_id_number: data.personal_id_number || '',
        language: data.language || 'en',
        bio: data.bio || '',
        date_of_birth: data.date_of_birth || '',
      });
    } catch (err) {
      setProfileError(t('common.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      setProfileError('');
      await authService.updateProfile(formData);
      if (formData.language && formData.language !== i18n.language) {
        i18n.changeLanguage(formData.language);
      }
      setSnackbar({ open: true, message: t('profile.profileUpdated'), severity: 'success' });
    } catch (err) {
      const detail = err.response?.data;
      if (detail && typeof detail === 'object') {
        const msgs = Object.entries(detail)
          .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
          .join(' | ');
        setProfileError(msgs);
      } else {
        setProfileError(t('profile.failedUpdateProfile'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new_password1 !== passwordData.new_password2) {
      setPasswordError(t('profile.passwordsNoMatch'));
      return;
    }
    try {
      setChangingPassword(true);
      setPasswordError('');
      await authService.changePassword(
        passwordData.old_password,
        passwordData.new_password1,
        passwordData.new_password2,
      );
      setPasswordData({ old_password: '', new_password1: '', new_password2: '' });
      setSnackbar({ open: true, message: t('profile.passwordChanged'), severity: 'success' });
    } catch (err) {
      const detail = err.response?.data;
      if (detail && typeof detail === 'object') {
        const msgs = Object.values(detail).flat().join(' ');
        setPasswordError(msgs);
      } else {
        setPasswordError(t('profile.failedChangePassword'));
      }
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ py: 2, maxWidth: 640 }}>
        <Typography variant="h4" sx={{ mb: 3 }}>{t('profile.title')}</Typography>

        {/* Profile info */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>{t('profile.personalInfo')}</Typography>

          {profile?.date_joined && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('profile.memberSince', { date: new Date(profile.date_joined).toLocaleDateString() })}
            </Typography>
          )}

          {profileError && <Alert severity="error" sx={{ mb: 2 }}>{profileError}</Alert>}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('auth.username')}
                name="username"
                value={formData.username}
                onChange={handleProfileChange}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label={t('profile.firstName')}
                name="first_name"
                value={formData.first_name}
                onChange={handleProfileChange}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label={t('profile.lastName')}
                name="last_name"
                value={formData.last_name}
                onChange={handleProfileChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('common.email')}
                name="email"
                type="email"
                value={formData.email}
                onChange={handleProfileChange}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label={t('profile.phoneNumber')}
                name="phone_number"
                value={formData.phone_number}
                onChange={handleProfileChange}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label={t('profile.personalId')}
                name="personal_id_number"
                value={formData.personal_id_number}
                onChange={handleProfileChange}
                helperText={t('profile.personalIdHelper')}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label={t('profile.dateOfBirth')}
                name="date_of_birth"
                type="date"
                value={formData.date_of_birth}
                onChange={handleProfileChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label={t('profile.language')}
                name="language"
                value={formData.language}
                onChange={handleProfileChange}
              >
                {LANGUAGES.map((l) => (
                  <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('profile.bio')}
                name="bio"
                multiline
                rows={3}
                value={formData.bio}
                onChange={handleProfileChange}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleSaveProfile}
              disabled={saving}
            >
              {saving ? t('common.saving') : t('profile.saveChanges')}
            </Button>
          </Box>
        </Paper>

        {/* Password change */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>{t('profile.changePassword')}</Typography>

          {passwordError && <Alert severity="error" sx={{ mb: 2 }}>{passwordError}</Alert>}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('profile.currentPassword')}
                name="old_password"
                type="password"
                value={passwordData.old_password}
                onChange={handlePasswordChange}
                autoComplete="current-password"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label={t('profile.newPassword')}
                name="new_password1"
                type="password"
                value={passwordData.new_password1}
                onChange={handlePasswordChange}
                autoComplete="new-password"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label={t('profile.confirmNewPassword')}
                name="new_password2"
                type="password"
                value={passwordData.new_password2}
                onChange={handlePasswordChange}
                autoComplete="new-password"
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handleChangePassword}
              disabled={changingPassword}
            >
              {changingPassword ? t('profile.changingPassword') : t('profile.changePassword')}
            </Button>
          </Box>
        </Paper>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}

export default Profile;
